/**
 * @fileoverview Shared password-policy rule evaluation.
 * @description Evaluates a fetched password policy against a candidate password value. Used by
 * the live rule checklist (PasswordPolicyHints) and by the set-password and change-password
 * schemas' submit-time validation, so the two can never drift apart — a password the checklist
 * shows as fully compliant is guaranteed to be the same password the schema accepts, and vice
 * versa. Each check mirrors the identity backend's PasswordPolicyValidator exactly: length counts
 * Unicode code points (an emoji or accented letter counts once), and the character classes are
 * Unicode-aware, with "special" meaning anything that isn't a letter, number, or whitespace. The
 * email/name/history rules (disallowEmail, disallowName, previousPasswordsChecked) aren't
 * evaluated here — the forms don't always know the account's email or name, and history can only
 * ever be checked server-side — so those still surface as the API's 1034 error.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { PasswordPolicy } from '@/features/auth/services/password-policy.service'

export type PasswordPolicyRuleKey = 'length' | 'maxLength' | 'upper' | 'lower' | 'digit' | 'special'

export interface PasswordPolicyRuleResult {
  key: PasswordPolicyRuleKey
  met: boolean
}

const UPPERCASE_PATTERN = /\p{Lu}/u
const LOWERCASE_PATTERN = /\p{Ll}/u
const DIGIT_PATTERN = /\p{Nd}/u
const SPECIAL_PATTERN = /[^\p{L}\p{N}\s]/u

/**
 * @description Evaluates a candidate password against each rule the given policy actually
 * requires (a rule the policy doesn't require is left out entirely, not included as "met"). The
 * maximum-length rule is only included once the password exceeds it, so the checklist doesn't
 * carry a permanently-green "at most 128 characters" row.
 * @param {PasswordPolicy} policy - The fetched password policy.
 * @param {string} value - The candidate password.
 * @returns {PasswordPolicyRuleResult[]} Only the rules this policy requires, each flagged met/not.
 */
export function evaluatePasswordPolicy(
  policy: PasswordPolicy,
  value: string,
): PasswordPolicyRuleResult[] {
  const length = [...value].length
  const rules: PasswordPolicyRuleResult[] = [{ key: 'length', met: length >= policy.minLength }]

  if (length > policy.maxLength) {
    rules.push({ key: 'maxLength', met: false })
  }

  if (policy.requireUppercase) {
    rules.push({ key: 'upper', met: UPPERCASE_PATTERN.test(value) })
  }

  if (policy.requireLowercase) {
    rules.push({ key: 'lower', met: LOWERCASE_PATTERN.test(value) })
  }

  if (policy.requireNumber) {
    rules.push({ key: 'digit', met: DIGIT_PATTERN.test(value) })
  }

  if (policy.requireSpecial) {
    rules.push({ key: 'special', met: SPECIAL_PATTERN.test(value) })
  }

  return rules
}

/**
 * @description Whether a candidate password satisfies every rule the given policy requires.
 * @param {PasswordPolicy} policy - The fetched password policy.
 * @param {string} value - The candidate password.
 * @returns {boolean} True only if every rule the policy requires is met.
 */
export function passwordMeetsPolicy(policy: PasswordPolicy, value: string): boolean {
  return evaluatePasswordPolicy(policy, value).every((rule) => rule.met)
}
